import { Component, input, signal, computed, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { IngestionPoint } from "../data-ingestion/data.ingestion.service";
import { S25RuleTreeModule } from "../s25-rule-tree/s25.rule.tree.module";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { INGESTION_POINT_TYPE_ID } from "../data-ingestion/data.ingestion.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";

@TypeManagerDecorator("workflow-stage-rules")
@Component({
    selector: "workflow-stage-rules",
    template: `
        <div class="stage-rules-container">
            @if (selectedStage()) {
                <div class="rules-header">
                    <h3>Rules for {{ selectedStage().stageName }}</h3>
                </div>

                <div class="form-selector">
                    <label>Form:</label>
                    <div class="form-controls">
                        @if (refreshKey()) {
                            <s25-ingestion-point-dropdown
                                [chosen]="selectedFormDropdownItem()"
                                [enabledOnly]="true"
                                (chosenChange)="onFormSelected($event)"
                                class="form-dropdown"
                            ></s25-ingestion-point-dropdown>
                        }
                        <s25-ng-button
                            type="secondary"
                            buttonText="Refresh"
                            [onClick]="refreshForms"
                            buttonClass="refresh-btn"
                        ></s25-ng-button>
                    </div>
                </div>

                @if (selectedFormId()) {
                    <div class="rules-editor">
                        @if (selectedStage().workflowStageId) {
                            <s25-ng-rule-tree
                                [category]="'workflowStage'"
                                [subCategory]="subCategory()"
                                [conditionFilterMap]="workflowStageConditionFilterMap()"
                                class="rule-tree-editor"
                            ></s25-ng-rule-tree>
                        } @else {
                            <div class="unsaved-stage-warning">
                                <p>
                                    <strong>Stage Not Saved:</strong> Please save the workflow process first before
                                    configuring rules for this stage.
                                </p>
                            </div>
                        }
                    </div>
                } @else {
                    <div class="no-form-selected">
                        <p>Select a form to configure stage rules.</p>
                    </div>
                }
            } @else {
                <div class="no-stage-selected">
                    <p>No stage selected.</p>
                </div>
            }
        </div>
    `,
    styles: [
        `
            .stage-rules-container {
                padding: 20px;
            }

            .rules-header h3 {
                margin: 0 0 16px 0;
                color: #333;
            }

            .form-selector {
                margin-bottom: 20px;
                padding: 16px;
                background: #f8f9fa;
                border-radius: 6px;
                border: 1px solid #e0e0e0;
            }

            .form-selector label {
                display: block;
                font-weight: 500;
                margin-bottom: 8px;
                color: #555;
            }

            .form-controls {
                display: flex;
                gap: 10px;
                align-items: center;
            }

            .form-dropdown {
                flex: 1;
                min-width: 200px;
            }

            .refresh-btn {
                flex-shrink: 0;
            }

            .rules-editor {
                background: white;
                border: 1px solid #e0e0e0;
                border-radius: 6px;
                overflow: hidden;
            }

            .rule-tree-editor {
                padding: 20px;
            }

            .no-form-selected,
            .no-stage-selected {
                text-align: center;
                padding: 40px 20px;
                color: #666;
            }

            .unsaved-stage-warning {
                color: #856404;
                background: #fff3cd;
                border: 1px solid #ffeaa7;
                border-radius: 6px;
                padding: 16px;
                text-align: center;
            }

            .unsaved-stage-warning p {
                margin: 0;
                font-size: 14px;
            }
        `,
    ],
    standalone: true,
    imports: [CommonModule, FormsModule, S25RuleTreeModule, S25ButtonComponent, S25DropdownPaginatedModule],
})
export class WorkflowStageRulesComponent {
    selectedStage = input<WorkflowTypes.WorkflowStage | null>(null);
    workflowProcessId = input<number | null>(null);

    selectedFormId = signal<number | null>(null);
    selectedFormDropdownItem = signal<DropDownItem | null>(null);
    refreshKey = signal<number>(1);

    subCategory = computed(() => {
        if (!this.selectedStage() || !this.workflowProcessId() || !this.selectedFormId()) {
            return "";
        }
        // Format: stageId & parentItemTypeId & parentItemId
        return `${this.selectedStage().workflowStageId}&${INGESTION_POINT_TYPE_ID}&${this.selectedFormId()}`;
    });

    // Condition filter map for workflowStage rules - uses ingestion fields for the selected form
    workflowStageConditionFilterMap = computed(() => {
        if (!this.selectedFormId()) return {};
        return {
            ingestionFieldsAll: "" + this.selectedFormId(),
        };
    });

    constructor(private cd: ChangeDetectorRef) {}

    refreshForms = () => {
        // Increment refreshKey to destroy and recreate the dropdown
        this.refreshKey.set(this.refreshKey() + 1);
    };

    onFormSelected(formItem: DropDownItem | null) {
        if (formItem && formItem.itemId) {
            this.selectedFormDropdownItem.set(formItem);
            this.selectedFormId.set(parseInt(formItem.itemId.toString()));
        } else {
            this.selectedFormDropdownItem.set(null);
            this.selectedFormId.set(null);
        }
        this.cd.detectChanges();
    }

    getSelectedFormName(): string {
        const selectedItem = this.selectedFormDropdownItem();
        return selectedItem?.itemName || "Unknown Form";
    }
}
